import type { ActivityReport } from '@wms/domain';
import { api } from '../../api';
import { fmt } from '../../format';
import { addDays, durationText, percentText } from '../../performance';
import { useSession } from '../../session';
import type { SellerSummary } from '../../types';
import { ErrorBanner, Spinner, useLoad } from '../../ui';
import { Icon, type IconName } from '../../ui/icons';

interface Tile {
  readonly label: string;
  readonly value: string;
  readonly hint: string;
  readonly href: string;
  readonly alert?: boolean;
}

interface TileGroup {
  readonly title: string;
  readonly icon: IconName;
  readonly tiles: readonly Tile[];
}

/** Son 30 gün: how fast the orders left and how many came back (Performans has the rest). */
function lastDays(a: ActivityReport): TileGroup {
  return {
    title: 'Son 30 gün',
    icon: 'chart',
    tiles: [
      { label: 'Kargoya verilen', value: fmt.n(a.orders.shipped), hint: `${fmt.n(a.orders.received)} sipariş alındı, ${fmt.n(a.orders.delivered)} teslim edildi`, href: '#/performans' },
      {
        label: 'Kargoya verme süresi',
        value: durationText(a.speed.medianHours),
        hint: `Ortanca · 24 saat içinde kargoda: ${percentText(a.speed.within24hRate)}`,
        href: '#/performans',
      },
      { label: 'Teslim süresi', value: durationText(a.speed.deliveryMedianHours), hint: 'Ortanca; kargodan alıcıya', href: '#/performans' },
      { label: 'İade oranı', value: percentText(a.returns.rate), hint: `Kargoya verilenlerden ${fmt.n(a.returns.shippedReturned)} siparişin iadesi geldi`, href: '#/performans' },
    ],
  };
}

/** The seller's start screen: orders, stock, inbound, documents, marketplaces and money. */
export function SellerHome() {
  const { me } = useSession();
  const summary = useLoad(() => api<SellerSummary>('GET', '/api/portal/summary'), [], 20_000);
  // the last 30 days of Performans, once per visit (a slower query than the summary)
  const recent = useLoad(() => {
    const today = fmt.today();
    return api<ActivityReport>('GET', `/api/performance/activity?from=${addDays(today, -29)}&to=${today}`);
  }, []);
  if (summary.error) return <ErrorBanner message={summary.error} onRetry={() => void summary.reload()} />;
  if (!summary.data) return <Spinner />;
  const s = summary.data;
  const o = (...statuses: (keyof SellerSummary['orders'])[]) => statuses.reduce((n, k) => n + (s.orders[k] ?? 0), 0);
  const failing = s.marketplaces.auth_failed ?? 0;
  const idle = s.marketplacesIdle ?? 0;

  const groups: readonly TileGroup[] = [
    {
      title: 'Siparişler',
      icon: 'list',
      tiles: [
        { label: 'Bekleyen', value: fmt.n(o('pending', 'backordered', 'on_hold')), hint: 'Rezerve, stok veya karar bekliyor', href: '#/siparisler', alert: o('on_hold') > 0 },
        { label: 'Depoda hazırlanıyor', value: fmt.n(o('allocated', 'picking', 'picked', 'packing')), hint: 'Toplanıyor veya paketleniyor', href: '#/siparisler' },
        { label: 'Kargoya hazır', value: fmt.n(o('packed')), hint: 'Paketlendi, kargo teslimi bekliyor', href: '#/siparisler' },
        { label: 'Bugün kargolanan', value: fmt.n(s.shippedToday), hint: 'Kargo firmasına teslim edildi', href: '#/siparisler' },
      ],
    },
    ...(recent.data ? [lastDays(recent.data)] : []),
    {
      title: 'Stok ve mal kabul',
      icon: 'inbox',
      tiles: [
        { label: 'Kullanılabilir stok', value: fmt.n(s.stock.available), hint: `${fmt.n(s.stock.skus)} üründe, ${fmt.n(s.stock.physical)} adet depoda`, href: '#/urunler' },
        { label: 'Stoğu biten ürün', value: fmt.n(s.stock.outOfStock), hint: 'Yeni gönderi planlayın', href: '#/urunler?stok=tukendi', alert: s.stock.outOfStock > 0 },
        {
          label: "SKT'si yaklaşan ürün",
          value: fmt.n(s.stock.expiring),
          hint: '30 gün içinde dolan, raf ömrü yetmeyen veya geçmiş lotlar',
          href: '#/urunler?skt=yakin',
          alert: s.stock.expiring > 0,
        },
        { label: 'Yoldaki gönderi', value: fmt.n(s.inbound.expected ?? 0), hint: `${fmt.n(s.inbound.draft ?? 0)} taslak · ${fmt.n(s.inbound.receiving ?? 0)} kabulde`, href: '#/asn' },
      ],
    },
    {
      title: 'Belgeler ve entegrasyon',
      icon: 'doc',
      tiles: [
        { label: 'Belge bekleyen', value: fmt.n(s.documentsPending), hint: 'Zorunlu yaptığınız belge olmadan paketlenmez', href: '#/e-belge', alert: s.documentsPending > 0 },
        {
          label: 'Pazaryeri hesapları',
          value: fmt.n(s.marketplaces.active ?? 0),
          hint: failing > 0 ? `${failing} hesapta yetki hatası` : idle > 0 ? `${idle} hesapta sipariş alımı başlamadı` : 'Bağlı ve çalışıyor',
          href: '#/pazaryerleri',
          alert: failing > 0 || idle > 0,
        },
      ],
    },
    {
      title: 'Depo ücretleri',
      icon: 'receipt',
      tiles: [
        { label: 'Bu ay biriken', value: fmt.money(s.billing.currentMonth), hint: 'Henüz faturalanmamış hizmetler (KDV hariç)', href: '#/faturalar' },
        s.billing.lastInvoice
          ? {
              label: 'Son hizmet faturası',
              value: fmt.money(s.billing.lastInvoice.total),
              hint: `${s.billing.lastInvoice.invoiceNumber} · ${fmt.month(s.billing.lastInvoice.periodStart)}`,
              href: `#/faturalar/${s.billing.lastInvoice.id}`,
            }
          : { label: 'Son hizmet faturası', value: '—', hint: 'Henüz fatura kesilmedi', href: '#/faturalar' },
      ],
    },
  ];

  return (
    <>
      <div className="greeting">
        <h2>Merhaba {me.fullName.split(' ')[0]}</h2>
        <p className="muted">{me.tenant?.name} hesabınızın depodaki durumu aşağıda.</p>
      </div>
      {groups.map((g) => (
        <section key={g.title} className="stack">
          <h2 className="section-h">
            <span className="section-icon">
              <Icon name={g.icon} size={16} />
            </span>
            {g.title}
          </h2>
          <div className="grid tiles">
            {g.tiles.map((t) => (
              <a key={t.label} href={t.href} className={`card kpi${t.alert ? ' alert' : ''}${/^0([,.]0+)?$/.test(t.value) ? ' zero' : ''}`}>
                <span className="n">{t.value}</span>
                <span className="l">{t.label}</span>
                <span className="h">{t.hint}</span>
              </a>
            ))}
          </div>
        </section>
      ))}
    </>
  );
}
